import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  UntypedFormBuilder,
  UntypedFormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators
} from '@angular/forms';
import {LocationDetails} from '@models/locationDetails/location-details';
import {GeocodingService} from '@services/administration/machine/geocoding/geocoding.service';
import {LocationType} from '@constants/machine/location-type';
import {NotificationService} from '@services/notification/notification.service';
import {LocationDetailsMapProperties} from '@models/locationDetails/location-details-map-properties';

type ChangeCallbackFn<T> = (value: T) => void;
type TouchCallbackFn = () => void;

@Component({
    selector: 'scitis-machine-location',
    templateUrl: './machine-location.component.html',
    styleUrls: ['./machine-location.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MachineLocationComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => MachineLocationComponent),
            multi: true
        }
    ],
    standalone: false
})

export class MachineLocationComponent implements ControlValueAccessor, Validator, OnInit, OnChanges, AfterViewInit {
  private CURRENT_PAGE_NAME: string = 'MachineLocationComponent';

  LONGITUDE_MAX: number = 180;
  LONGITUDE_MIN: number = -180;
  LATITUDE_MAX: number = 90;
  LATITUDE_MIN: number = -90;

  public mapSettings: LocationDetailsMapProperties = new LocationDetailsMapProperties();

  constructor(private formBuilder: UntypedFormBuilder,
              private geocodingService: GeocodingService,
              private notification: NotificationService) {

    this.locationDetailsFormGroup = this.formBuilder.group({
      addressSearchInput: [''],
      latitudeSearchInput: ['', [Validators.min(this.LATITUDE_MIN), Validators.max(this.LATITUDE_MAX)]],
      longitudeSearchInput: ['', [Validators.min(this.LONGITUDE_MIN), Validators.max(this.LONGITUDE_MAX)]],
      country: [this.locationDetails.country],
      countryCode: [this.locationDetails.countryCode],
      regionCity: [this.locationDetails.regionCity],
      street: [this.locationDetails.street],
      number: [this.locationDetails.number],
      postalCode: [this.locationDetails.postalCode],
      latitude: [this.locationDetails.latitude, [Validators.min(this.LATITUDE_MIN), Validators.max(this.LATITUDE_MAX)]],
      longitude: [this.locationDetails.longitude, [Validators.min(this.LONGITUDE_MIN), Validators.max(this.LONGITUDE_MAX)]]
    });
  }

  @Input()
  submitted: boolean = false;

  @Input()
  machineName: string = '';

  @Input()
  locationDetails: LocationDetails = new LocationDetails();

  @Input()
  isAdminOrHigher: boolean = false;

  @Output()
  updatedLocationDetailsEvent = new EventEmitter<LocationDetails>();

  @Output()
  formIsValid = new EventEmitter<boolean>();

  locationDetailsFormGroup: UntypedFormGroup = this.formBuilder.group({});
  locationDetailsTypes = LocationType;
  selectedLocationDetailsType = LocationType.ADDRESS;
  public isSearchStarted: boolean = false;
  public isDetailsAreaCollapsed: boolean = true;

  // convenience getter for easy access to form fields
  get formControls() {
    return this.locationDetailsFormGroup.controls;
  }

  ngOnChanges(changes: SimpleChanges): void {
    const newLocationDetails: LocationDetails = changes?.locationDetails?.currentValue as LocationDetails;
    if (newLocationDetails !== null && newLocationDetails !== undefined) {
      this.fillLocationDetailsFormFields(newLocationDetails);
    }
  }

  registerOnChange(fn: ChangeCallbackFn<object>): void {
    this.locationDetailsFormGroup.valueChanges.subscribe(fn);
  }

  registerOnTouched(_fn: TouchCallbackFn): void {

  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.locationDetailsFormGroup.disable();
    } else {
      this.locationDetailsFormGroup.enable();
    }
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    if (this.locationDetailsFormGroup.valid) {
      return null;
    }
    return {invalidForm: {valid: false, message: 'location details fields are invalid'}};
  }

  writeValue(val: any): void {
    if (val) {
      this.locationDetailsFormGroup.setValue(val, {emitEvent: false});
    }
  }

  get addressIsSelected(): boolean {
    return this.selectedLocationDetailsType === LocationType.ADDRESS;
  }

  get coordinatesIsSelected(): boolean {
    return this.selectedLocationDetailsType === LocationType.COORDINATES;
  }

  setLocationDetailsType(locationType: LocationType) {
    this.selectedLocationDetailsType = locationType;
  }

  onInputValueChanged(): void {
    this.onLocationDetailsUpdated();
  }

  onSearch() {

    const address = this.formControls.addressSearchInput.value;
    const latitude = this.formControls.latitudeSearchInput.value + '';
    const longitude = this.formControls.longitudeSearchInput.value + '';

    const searchValue: string = this.geocodingService.getGeocodingSearchValue(this.addressIsSelected, address, latitude, longitude);

    this.isSearchStarted = true;
    this.isDetailsAreaCollapsed = false;
    this.geocodingService.getLocation(searchValue).subscribe((success: LocationDetails) => {
      this.fillLocationDetailsFormFields(success);
      this.onLocationDetailsUpdated();
      if (success.latitude && success.longitude) {
        this.mapSettings.setCoordinates(success.latitude, success.longitude, this.machineName);
      }
      this.isSearchStarted = false;

    }, (error) => {
      this.notification.error('There was an error geocoding ' + searchValue, error, this.CURRENT_PAGE_NAME, true);
      this.isSearchStarted = false;
    });
  }

  private fillLocationDetailsFormFields(locationDetails: LocationDetails) {
    this.formControls.country.setValue(locationDetails.country);
    this.formControls.countryCode.setValue(locationDetails.countryCode);
    this.formControls.regionCity.setValue(locationDetails.regionCity);
    this.formControls.street.setValue(locationDetails.street);
    this.formControls.number.setValue(locationDetails.number);
    this.formControls.postalCode.setValue(locationDetails.postalCode);
    this.formControls.latitude.setValue(locationDetails.latitude);
    this.formControls.longitude.setValue(locationDetails.longitude);
  }

  private onLocationDetailsUpdated() {
    const locationDetails: LocationDetails = new LocationDetails();
    locationDetails.country = this.formControls.country.value;
    locationDetails.countryCode = this.formControls.countryCode.value;
    locationDetails.regionCity = this.formControls.regionCity.value;
    locationDetails.street = this.formControls.street.value;
    locationDetails.number = this.formControls.number.value;
    locationDetails.postalCode = this.formControls.postalCode.value;
    locationDetails.latitude = this.formControls.latitude.value;
    locationDetails.longitude = this.formControls.longitude.value;

    this.updatedLocationDetailsEvent.emit(locationDetails);
    this.formIsValid.emit(this.locationDetailsFormGroup.valid);
  }

  onMapClick(event: any) {
    if (!this.isAdminOrHigher) {
      return;
    }
    this.isDetailsAreaCollapsed = false;
    this.isSearchStarted = true;
    console.log('onMapClick', event);
    const searchValue = event.latLng.lat() + ',' + event.latLng.lng();
    this.geocodingService.getLocation(searchValue).subscribe((success: LocationDetails) => {
      this.fillLocationDetailsFormFields(success);
      this.onLocationDetailsUpdated();
      if (success.latitude && success.longitude) {
        this.mapSettings.setCoordinates(success.latitude, success.longitude, this.machineName);
      }
      this.isSearchStarted = false;

    }, (error) => {
      this.notification.error('There was an error geocoding ' + searchValue, error, this.CURRENT_PAGE_NAME, true);
      this.isSearchStarted = false;
    });

  }

  ngOnInit(): void {
    if (this.locationDetails && this.locationDetails.latitude && this.locationDetails.longitude) {
      this.mapSettings.setCoordinates(this.locationDetails.latitude, this.locationDetails.longitude, this.machineName);
    }
    if (!this.isAdminOrHigher) {
      this.locationDetailsFormGroup.disable();
      this.mapSettings.options.mapTypeControl = false;
    }
  }

  @ViewChild('addressSearchInput')
  public searchElementRef: ElementRef | any;

  ngAfterViewInit() {
    const options: google.maps.places.AutocompleteOptions = {
      types: ['geocode'],
      fields: ['geometry', 'address_component']
    };
    const autocomplete = new google.maps.places.Autocomplete(this.searchElementRef.nativeElement, options);
    google.maps.event.addListener(autocomplete, 'place_changed', () => {
      this.isDetailsAreaCollapsed = false;
      const place = autocomplete.getPlace();
      const locationDetails = this.geocodingService.parsePlaceResultToLocationDetails(place);
      this.fillLocationDetailsFormFields(locationDetails);
      this.onLocationDetailsUpdated();
      if (locationDetails.latitude && locationDetails.longitude) {
        this.mapSettings.setCoordinates(locationDetails.latitude, locationDetails.longitude, this.machineName);
      }
      console.log(place);
    });
  }
}
